import {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {useAppSelector} from "hooks/useAppSelector";
import {Channel, Message, UserBlacklistInfo, UserStatsMap, UserInfoShort, useWebSocketContext} from "app/websocket";
import {TComposerState} from "../types";
import {setBlockedUsers} from "app/store/slices/chat";
import pkg from 'lodash';
import { useDispatch } from "react-redux";
const {filter, some} = pkg;

export const useChatMessages = () => {
    const userData = useAppSelector((state) => state.user.user);
    const loginStatus = useAppSelector((state) => state.user.isLogin);
    const {listChannelBans, listChat, sendMessage, lastJsonMessage, isConnection, userInfo, messageQueue, setMessageQueue} = useWebSocketContext();
    const dispatch = useDispatch();

    const [channelId, setChannelId] = useState<number>();
    const [channelList, setChannelList] = useState<Channel[]>([]);
    const [messages, setMessages] = useState<Message[]>([]);
    const [users, setUsers] = useState<UserInfoShort[]>([]);
    const [blacklistUsers, setBlacklistUsers] = useState<UserBlacklistInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const lastChannelId = useRef<number | undefined>();
    const [usersData, setUsersData] = useState<UserStatsMap>({});
    const level = useAppSelector((state) => state.user.user?.level);

    // Для контроля загрузки старых сообщений
    const [isLoadOldMessages, setIsLoadOldMessages] = useState(false);

    // Оптимизация: idsLoaded как useRef, чтобы не вызывать ререндер
    const idsLoaded = useRef<Set<number | string>>(new Set());

    // Блокировка пользователей
    useEffect(() => {
        const matchedUsers = filter(users, (user) => some(blacklistUsers, {user_id: user.user_id}));
        dispatch(setBlockedUsers(matchedUsers));
    }, [users, blacklistUsers]);

    const currentChannel = channelId !== undefined
        ? channelList.find((c) => c.channel_id === +channelId)
        : undefined;

    const platformConfig = useAppSelector((state) => state.config.platformConfig);
    const vipList = platformConfig?.vipList || [];

    // Состояние composer
    const composerState = useMemo<TComposerState | null>(() => {
        if (channelId && listChannelBans.includes(channelId)) {
            return {type: "permanent-ban", date: undefined};
        }
        if ((level || 0) < currentChannel?.vip_level) {
            const disabledLevel = vipList?.find((v) => v.level === currentChannel?.vip_level);
            return {type: "locked", date: undefined, text: disabledLevel?.name};
        }
        return null;
    }, [channelId, listChannelBans, level, currentChannel]);

    // Кнопка отправки
    const disableSendButton = useMemo(() => {
        if (!userInfo) return true;
        if (channelId) {
            const currentChannel = channelList.find((c) => c.channel_id === +channelId);
            if (
                currentChannel &&
                (userInfo.level < currentChannel.vip_level ||
                    userInfo.bet_amount < currentChannel.bet_amount ||
                    userInfo.deposit_amount < currentChannel.deposit_amount)
            ) {
                return true;
            }
        }
        return false;
    }, [channelId, channelList, userInfo]);

    // Блокировка/разблокировка пользователя
    const handleBlockUser = useCallback(
        (user: UserInfoShort | undefined) => {
            if (String(userData?.userId) !== String(user?.user_id)) {
                sendMessage(JSON.stringify({m: "chat.blacklist.add", p: {user_id: user?.user_id}}));
            }
        },
        [userData, sendMessage],
    );

    const handleUnBlockUser = useCallback(
        (user: UserInfoShort | undefined) => {
            sendMessage(JSON.stringify({m: "chat.blacklist.del", p: {user_id: user?.user_id}}));
        },
        [sendMessage],
    );

    const handleGetInfoUserById = useCallback(
        (userId: number) => {
            sendMessage(JSON.stringify({m: "user.info", v: "1.0", t: Date.now(), p: {user_id: userId}}));
        },
        [sendMessage],
    );

    // Получение старых сообщений
    const handleGetMoreMessages = useCallback(
        (id: number | string) => {
            if (idsLoaded.current.has(id)) return;
            setIsLoadOldMessages(true);
            sendMessage(
                JSON.stringify({
                    m: "chat.talk.list",
                    v: "1.0",
                    t: Date.now(),
                    p: {channel_id: lastChannelId.current, id, count: 50},
                }),
            );
            idsLoaded.current.add(id);
        },
        [sendMessage],
    );

    const isEmptyMessage = (msg: string) => {
        const cleaned = msg
            .replace(/&nbsp;/g, "")
            .replace(/<br\s*\/?>/gi, "") // удаляем все <br>, <br/>, <br />
            .replace(/\s+/g, "");
        return cleaned.length > 0;
    };

    const [isDisabledSendButton, setIsDisabledSendButton] = useState(false);

    // Отправка сообщения
    const handleSendMessage = useCallback(
        (msg: any, onSuccess: () => void) => {
            if (!loginStatus) return;
            const trimmedMessage = msg.trim();
            if (isEmptyMessage(trimmedMessage)) {
                sendMessage(JSON.stringify({m: "chat.talk", p: {channel_id: lastChannelId.current, content: trimmedMessage, t: Date.now()}}));
                onSuccess();
                setIsDisabledSendButton(true);
                setTimeout(() => {
                    setIsDisabledSendButton(false)
                }, 3000)
            }
        },
        [loginStatus, sendMessage],
    );

    // Фильтрация сообщений
    const filteredMessages = useMemo(() => {
        if (!messages?.length) return [];
        if (!blacklistUsers?.length) return messages;
        const blacklistedIds = new Set(blacklistUsers.map((user) => user.user_id));
        return messages.filter((msg) => !blacklistedIds.has(msg.user_info.user_id)).filter((msg) => msg.channel_id === lastChannelId.current);
    }, [messages, blacklistUsers]);

    const shouldInit = userInfo && isConnection;

    useEffect(() => {
        if (!shouldInit) return;
        sendMessage(JSON.stringify({m: "channel.list"}));
        sendMessage(JSON.stringify({m: "chat.blacklist"}));

        return () => {
            if (lastChannelId.current) {
                sendMessage(JSON.stringify({m: "channel.leave", p: {channel_id: lastChannelId.current}}));
            }
        };
    }, [shouldInit]);

    // Обработка очереди сообщений
    useEffect(() => {
        if (!messageQueue.length) return;

        messageQueue.forEach((msg) => {
            switch (msg.m) {
                case "channel.list":
                    if (Array.isArray(msg.p.list) && msg.p.list.length > 0) {
                        setChannelList(msg.p.list);
                        setChannelId(msg.p.list[0].channel_id);
                    }
                    break;
                case "channel.user":
                    if (Array.isArray(msg.p) && msg.p.length > 0) setUsers(msg.p);
                    break;
                case "user.info":
                    setUsersData((prev) => ({...prev, [msg.p.user_id]: msg.p}));
                    break;
                case "chat.blacklist":
                    if (Array.isArray(msg.p) && msg.p.length > 0) setBlacklistUsers(msg.p);
                    break;
                case "channel.join.success":
                    sendMessage(JSON.stringify({m: "chat.talk.list", p: {channel_id: lastChannelId.current}}));
                    break;
                case "chat.talk.list": {
                    setIsLoading(false);
                    const newMessages: Message[] = [...messages, ...msg.p.list].sort((a, b) => a.ts - b.ts);

                    if (Array.isArray(newMessages)) {
                        const chatWrapper = document.querySelector(".chat-wrapper-body");
                        const prevScrollHeight = chatWrapper?.scrollHeight || 0;
                        setMessages((prevMessages) => {
                            const all = [...prevMessages, ...msg.p.list];
                            const unique = Array.from(new Map(all.map((m) => [m.id, m])).values());
                            return unique.sort((a, b) => a.ts - b.ts);
                        });
                        if (msg.p.list && messages.length && msg.p.list[0]?.ts < messages[10]?.ts) {
                            setTimeout(() => {
                                const newScrollHeight = chatWrapper?.scrollHeight || 0;
                                if (chatWrapper) {
                                    window.requestAnimationFrame(() => {
                                        chatWrapper.scrollTop = newScrollHeight - prevScrollHeight;
                                    });
                                }
                                setTimeout(() => setIsLoadOldMessages(false), 100);
                            }, 0);
                        } else {
                            setIsLoadOldMessages(false);
                        }
                    }
                    break;
                }
                case "chat.talk":
                    setMessages((prev) => [...prev, msg.p as Message]);
                    break;
                default:
                    break;
            }
        });

        // @ts-ignore
        setMessageQueue((prev) => prev.slice(messageQueue.length));
    }, [messageQueue, sendMessage, setMessageQueue, messages]);

    // Обработка кликов по юзерам
    useEffect(() => {
        if (!isLoading && messages.length) {
            setTimeout(() => {
                const wrapper = document.querySelector(".chat-wrapper-body");
                const msg = wrapper?.querySelectorAll(".chat-wrapper-comment");
                msg?.forEach((item) => {
                    const links = item.querySelectorAll("a[data-user-id]:not([data-init])");
                    links.forEach((el) => {
                        el.addEventListener("click", (e) => {
                            e.preventDefault();
                            el.setAttribute("data-init", "true");
                            const userId = el.getAttribute("data-user-id");
                            if (userId) {
                                // TODO: Open user info modal with userId
                            }
                        });
                    });
                });
            }, 100);
        }
    }, [messages, isLoading]);

    // Переключение каналов
    useEffect(() => {
        if (lastChannelId.current) {
            sendMessage(JSON.stringify({m: "channel.leave", p: {channel_id: lastChannelId.current}}));
            setIsLoading(true);
            setMessages([]);
        }
        if (channelId) {
            sendMessage(JSON.stringify({m: "channel.join", p: {channel_id: +channelId}}));
            lastChannelId.current = +channelId;
        }
    }, [channelId, sendMessage]);

    return {
        messages: filteredMessages,
        disableSendButton,
        handleSendMessage,
        users,
        isLoading,
        handleBlockUser,
        handleUnBlockUser,
        listChat,
        setChannelId,
        channelId,
        listChannelBans,
        composerState,
        handleGetInfoUserById,
        usersData,
        blacklistUsers,
        handleGetMoreMessages,
        isLoadOldMessages,
        isDisabledSendButton
    };
};
